import { useEffect, useRef, useState } from "react";
import type { Deal } from "../types";
import { analyzeAddresses, parseAddresses, parseCsvAddresses, type BulkResult } from "../lib/bulk";
import { money0, pct, x2 } from "../lib/format";
import { isCloud } from "../lib/supabase";
import {
  defaultBuyBox, matchesBuyBox, buyBoxMisses, buyBoxActive,
  listBuyBoxes, saveBuyBox, deleteBuyBox, newBuyBoxId,
  type BuyBox, type PropType,
} from "../lib/buybox";

const SAMPLE = "400 N Ashley Dr, Tampa, FL 33602\n337 Rowan Ct, Jacksonville, FL\n908 E 26th Ave, Tampa, FL";

export function DealFinder({ onImport }: { onImport: (deals: Deal[]) => void }) {
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  const [prog, setProg] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<BulkResult[] | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [fileMsg, setFileMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // buy box — the filters that define the deals you want
  const [box, setBox] = useState<BuyBox>(defaultBuyBox);
  const [saved, setSaved] = useState<BuyBox[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  useEffect(() => { listBuyBoxes().then(setSaved).catch(() => {}); }, []);
  const patchBox = (p: Partial<BuyBox>) => setBox((b) => ({ ...b, ...p }));

  const active = buyBoxActive(box);
  const shown = results && active && !showAll
    ? results.filter((r) => matchesBuyBox(r.deal, r.metrics, box))
    : results;
  const matchCount = results && active
    ? results.filter((r) => matchesBuyBox(r.deal, r.metrics, box)).length
    : (results?.length ?? 0);

  async function persistBox() {
    const toSave = box.id ? box : { ...box, id: newBuyBoxId() };
    await saveBuyBox(toSave);
    setSaved(await listBuyBoxes());
    setSavedMsg(`Saved "${toSave.name}".`);
  }
  async function removeBox() {
    if (!box.id) return;
    await deleteBuyBox(box.id);
    setSaved(await listBuyBoxes());
    setBox(defaultBuyBox());
    setSavedMsg("Deleted.");
  }

  const count = parseAddresses(text).length;

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const addrs = parseCsvAddresses(await f.text());
      setText((prev) => (prev.trim() ? prev.trim() + "\n" : "") + addrs.join("\n"));
      setFileMsg(`Loaded ${addrs.length} address${addrs.length === 1 ? "" : "es"} from ${f.name}`);
    } catch {
      setFileMsg("Couldn't read that file.");
    }
    if (fileRef.current) fileRef.current.value = "";
  }

  async function run() {
    const addrs = parseAddresses(text);
    if (!addrs.length) return;
    setRunning(true);
    setResults(null);
    setSel(new Set());
    setProg({ done: 0, total: addrs.length });
    const res = await analyzeAddresses(addrs, (done, total) => setProg({ done, total }));
    setResults(res);
    setRunning(false);
  }
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSel(new Set((shown ?? results ?? []).filter((r) => r.ok).map((r) => r.id)));
  const importSel = () => {
    const picked = (results ?? []).filter((r) => sel.has(r.id)).map((r) => r.deal);
    if (picked.length) { onImport(picked); setSel(new Set()); }
  };

  return (
    <div className="wrap">
      <div className="pe">
        <div className="l">◆ Bulk deal finder — one click, no copy-paste</div>
        <p>
          Paste a list of addresses from anywhere — <b>HUD Home Store, auctions, tax-deed lists, an agent's export</b> —
          and Beacon geocodes, pulls the <b>Section 8 rent ceiling, demographics &amp; flood zone</b>, runs the compliance
          gates, and scores every one. You get a <b>ranked shortlist</b> in seconds; add the winners to your portfolio and
          open them to enter price &amp; rents.
        </p>
      </div>

      {!isCloud() && (
        <div className="flag warn" style={{ marginBottom: 12 }}>
          <span>◐</span><div>Bulk lookup uses the cloud data engine. In local mode it only resolves the tract; sign in for the full scan.</div>
        </div>
      )}

      {/* BUY BOX — the filters that define the deals you want */}
      <details className="card pad bb" style={{ marginBottom: 14 }}>
        <summary className="bb-sum">
          <span className="eyebrow" style={{ margin: 0 }}>◆ Your buy box — the deals you actually want</span>
          <span className="tag t-watch">{active ? "Filtering on" : "Off — showing all"}</span>
        </summary>

        <div className="bb-grid" style={{ marginTop: 12 }}>
          <label className="bb-f"><span>Price min</span>
            <input type="number" placeholder="e.g. 500000" value={box.priceMin ?? ""} onChange={(e) => patchBox({ priceMin: e.target.value ? Number(e.target.value) : null })} /></label>
          <label className="bb-f"><span>Price max</span>
            <input type="number" placeholder="e.g. 1000000" value={box.priceMax ?? ""} onChange={(e) => patchBox({ priceMax: e.target.value ? Number(e.target.value) : null })} /></label>
          <label className="bb-f"><span>Property type</span>
            <select value={box.propertyType} onChange={(e) => patchBox({ propertyType: e.target.value as PropType })}>
              <option value="any">Any</option><option value="duplex">Duplex (2)</option><option value="triplex">Triplex (3)</option>
              <option value="fourplex">Fourplex (4)</option><option value="multi5">Multi (5+)</option>
            </select></label>
          <label className="bb-f"><span>Markets (comma-sep)</span>
            <input placeholder="Tampa, Jacksonville" value={box.markets.join(", ")} onChange={(e) => patchBox({ markets: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></label>
          <label className="bb-f"><span>Min score</span>
            <input type="number" min={0} max={100} placeholder="e.g. 70" value={box.minScore ?? ""} onChange={(e) => patchBox({ minScore: e.target.value ? Number(e.target.value) : null })} /></label>
          <label className="bb-f"><span>Min cap rate %</span>
            <input type="number" step="0.5" placeholder="e.g. 6.5" value={box.minCap != null ? box.minCap * 100 : ""} onChange={(e) => patchBox({ minCap: e.target.value ? Number(e.target.value) / 100 : null })} /></label>
          <label className="bb-f"><span>Min 2BR ceiling $</span>
            <input type="number" placeholder="e.g. 1800" value={box.minCeiling ?? ""} onChange={(e) => patchBox({ minCeiling: e.target.value ? Number(e.target.value) : null })} /></label>
          <label className="bb-f bb-check"><input type="checkbox" checked={box.floodXOnly} onChange={(e) => patchBox({ floodXOnly: e.target.checked })} /><span>No flood zone (X only)</span></label>
          <label className="bb-f bb-check"><input type="checkbox" checked={box.excludeGated} onChange={(e) => patchBox({ excludeGated: e.target.checked })} /><span>Hide gate-failed deals</span></label>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <input className="bb-name" value={box.name} onChange={(e) => patchBox({ name: e.target.value })} placeholder="Name this buy box" />
          <button className="btn btn-a btn-sm" onClick={persistBox}>💾 Save buy box</button>
          {box.id && saved.some((s) => s.id === box.id) && <button className="btn btn-g btn-sm" onClick={removeBox}>Delete</button>}
          {saved.length > 0 && (
            <select className="bb-name" value="" onChange={(e) => { const s = saved.find((x) => x.id === e.target.value); if (s) { setBox(s); setSavedMsg(`Loaded "${s.name}".`); } }}>
              <option value="">Load saved…</option>
              {saved.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <button className="btn btn-g btn-sm" onClick={() => { setBox(defaultBuyBox()); setSavedMsg(null); }}>Clear</button>
          {savedMsg && <span style={{ fontSize: 12, color: "var(--pass)" }}>✓ {savedMsg}</span>}
        </div>
        <div className="note" style={{ marginTop: 8 }}>
          These criteria filter your shortlist live. Price &amp; type get sharpest once a list carries them or a live source is wired — until then a fast address scan filters on score, market, flood, cap &amp; rent ceiling. Saved buy boxes will drive the daily auto-search.
        </div>
      </details>

      <div className="card pad" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
          <div className="eyebrow">Paste addresses — one per line</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {fileMsg && <span style={{ fontSize: 12, color: "var(--pass)" }}>✓ {fileMsg}</span>}
            <input ref={fileRef} type="file" accept=".csv,.txt" onChange={onFile} style={{ display: "none" }} />
            <button className="btn btn-g btn-sm" onClick={() => fileRef.current?.click()}>⬆ Upload CSV / list</button>
          </div>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={SAMPLE}
          rows={7}
          style={{ width: "100%", padding: 12, border: "1px solid var(--line)", borderRadius: 9, fontSize: 13, fontFamily: "inherit", background: "var(--white)", resize: "vertical" }}
        />
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
          <button className="btn btn-p" onClick={run} disabled={running || !count}>
            {running ? `Analyzing… ${prog.done}/${prog.total}` : `⚡ Analyze ${count || ""} address${count === 1 ? "" : "es"}`}
          </button>
          {!text && <button className="btn btn-g btn-sm" onClick={() => setText(SAMPLE)}>Use sample</button>}
          <span style={{ fontSize: 12, color: "var(--muted)" }}>Runs 4 at a time — a few seconds each.</span>
        </div>
        {running && <div className="bar" style={{ height: 8, marginTop: 12 }}><span style={{ width: (prog.total ? (prog.done / prog.total) * 100 : 0) + "%" }} /></div>}
      </div>

      {results && shown && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
            <h2 className="sechead" style={{ fontSize: 19, margin: 0 }}>
              Ranked shortlist — {matchCount}{active && matchCount !== results.length ? ` of ${results.length}` : ""}
            </h2>
            {active && (
              <button className="btn btn-g btn-sm" onClick={() => setShowAll((v) => !v)}>
                {showAll ? "Matching only" : "Show all"}
              </button>
            )}
            <div className="sp" style={{ flex: 1 }} />
            <button className="btn btn-g btn-sm" onClick={selectAll}>Select all</button>
            <button className="btn btn-a btn-sm" onClick={importSel} disabled={!sel.size}>Add {sel.size || ""} to portfolio</button>
          </div>
          {active && (
            <div className="note" style={{ marginBottom: 8 }}>
              Filtered by <b>{box.name || "your buy box"}</b>: {matchCount} of {results.length} match{showAll ? " — showing all, non-matches greyed" : ""}.
            </div>
          )}
          <div className="card pad">
            <div className="tablewrap">
              <table className="t">
                <thead>
                  <tr>
                    <th></th><th>#</th><th>Address</th><th>Score</th>
                    <th className="n">2BR ceiling</th><th>Flood</th><th className="n">Cap</th><th className="n">DSCR</th><th>Tract</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r, i) => {
                    const s = r.metrics.score;
                    const misses = active ? buyBoxMisses(r.deal, r.metrics, box) : [];
                    const off = misses.length > 0;
                    return (
                      <tr key={r.id} style={off ? { opacity: 0.5 } : undefined}>
                        <td><input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} disabled={!r.ok} /></td>
                        <td className="n">{i + 1}</td>
                        <td>
                          <div style={{ fontWeight: 600, color: "var(--forest)" }}>{r.matched ?? r.address}</div>
                          {!r.ok && <div style={{ fontSize: 11, color: "var(--fail)" }}>couldn't resolve</div>}
                          {off && <div style={{ fontSize: 11, color: "var(--brass-deep)" }}>✗ {misses.join(" · ")}</div>}
                        </td>
                        <td><span className={"tag t-" + (s.cls === "pass" ? "pass" : s.cls === "watch" ? "watch" : "fail")}>{s.gated ? "GATED" : s.composite}</span></td>
                        <td className="n">{r.deal.safmr2 ? money0(r.deal.safmr2) : "—"}</td>
                        <td>{r.deal.flood !== "X" ? <span className="tag t-watch">{r.deal.flood}</span> : <span style={{ color: "var(--muted)" }}>X</span>}</td>
                        <td className="n">{pct(r.metrics.capOnCost)}</td>
                        <td className="n">{x2(r.metrics.dscr)}</td>
                        <td className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>{r.deal.censusTract ?? "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="note" style={{ marginTop: 8 }}>
              This is a fast <b>location + rent-ceiling scan</b> on default financials — great for filtering a big list.
              Add the winners, then open each to enter the real price &amp; rents for the full underwrite, AI read, and projections.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
